import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { API_URL } from 'constant';
import { useNetwork } from 'hooks';

export type ChangeType = 'email' | 'phone';
export type Side = 'current' | 'next';
type SideState = 'pending' | 'approved';

export interface ChangeRequest {
	_id: string;
	type: ChangeType;
	current: string;
	next: string;
	countryCode?: string;
	expiresAt: string;
	sides?: Record<Side, SideState>;
	canResend?: Record<Side, boolean>;
}

export const msLeft = (iso: string) =>
	Math.max(0, new Date(iso).getTime() - Date.now());

/**
 * The card's only clock and its only source of pending state.
 *
 * Polling, not a socket. There is exactly one thing to learn — whether somebody
 * has clicked a link in another window or on their phone — and it is worth
 * knowing within half a minute, not within half a second. A socket for that is
 * infrastructure nobody would want to own.
 *
 * `expiresAt` is read from the server on every poll and NEVER recomputed as
 * "two hours from when I clicked". Resend moves it forward server-side; a
 * client-side clock would keep counting down from the original click and show
 * an expired ticket for a link that still works.
 */
export const useProfileChanges = () => {
	const { get, post, remove } = useNetwork();
	const [requests, setRequests] = useState<ChangeRequest[]>([]);
	const [loaded, setLoaded] = useState(false);

	/* Ticks the countdown. Kept separate from `requests` so a re-render each
	   minute never refetches, and a refetch never resets the clock. */
	const [, setTick] = useState(0);
	const pollRef = useRef<ReturnType<typeof setInterval>>();

	const refresh = useCallback(async () => {
		const resp: any = await get(API_URL.PROFILE_CHANGE_REQUESTS);
		const rows = Array.isArray(resp?.data) ? resp.data : [];
		setRequests(rows);
		setLoaded(true);
		return rows as ChangeRequest[];
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		refresh();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	/**
	 * STATE-AWARE POLLING, NOT A FLAT INTERVAL.
	 *
	 * A flat 30s is the wrong number for the one moment that matters: a ticket
	 * is on screen, the approval is being clicked on a phone right now, and the
	 * answer is wanted in seconds. A pending request lives minutes at most, so
	 * a 5s poll on a tiny endpoint costs nothing and reads as instant.
	 *
	 * Nothing pending → no polling at all. Tab hidden → back off to 30s, since
	 * `visibilitychange` will reconcile the moment it comes back anyway.
	 *
	 * `visibilitychange` as well as `focus`: focus misses some window-manager
	 * and desktop-switch transitions, visibility does not.
	 *
	 * This is deliberately not a socket. `admin.panel` holds no socket client
	 * today, so one would mean a client lib, an auth handshake, a namespace and
	 * a reconnect lifecycle — and it would not replace this loop, only sit
	 * beside it, because an all-day ops tab has to survive a socket that died
	 * silently on sleep/wake. A second channel with its own failure mode, to
	 * save a few seconds on one path, is the wrong trade.
	 */
	useEffect(() => {
		const outstanding = requests.length > 0;
		if (pollRef.current) clearInterval(pollRef.current);
		if (!outstanding) return undefined;

		const arm = () => {
			if (pollRef.current) clearInterval(pollRef.current);
			const hidden = document.visibilityState === 'hidden';
			pollRef.current = setInterval(refresh, hidden ? 30_000 : 5_000);
		};
		arm();

		const onWake = () => {
			refresh();
			arm();
		};
		window.addEventListener('focus', onWake);
		document.addEventListener('visibilitychange', onWake);

		const clock = setInterval(() => setTick(t => t + 1), 1000);
		return () => {
			if (pollRef.current) clearInterval(pollRef.current);
			clearInterval(clock);
			window.removeEventListener('focus', onWake);
			document.removeEventListener('visibilitychange', onWake);
		};
	}, [requests.length, refresh]);

	const byType = useMemo(() => {
		const live = requests.filter(r => msLeft(r.expiresAt) > 0);
		return {
			email: live.find(r => r.type === 'email'),
			phone: live.find(r => r.type === 'phone'),
		};
	}, [requests]);

	/** Expired locally the moment the clock passes, without waiting for a poll. */
	const expired = useMemo(
		() => ({
			email: requests.find(
				r => r.type === 'email' && msLeft(r.expiresAt) <= 0
			),
			phone: requests.find(
				r => r.type === 'phone' && msLeft(r.expiresAt) <= 0
			),
		}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[requests]
	);

	const request = useCallback(
		async (body: Record<string, unknown>) => {
			const resp: any = await post(API_URL.PROFILE_CHANGE_REQUESTS, body);
			const data = resp?.data ?? resp;
			if (data?.success) await refresh();
			return data;
		},
		[post, refresh]
	);

	/* Resend is the same POST with the same value — the server recognises it,
	   reuses the live code and restarts the window, so the mail already sitting
	   in the inbox keeps working. */
	const resend = useCallback(
		async (req: ChangeRequest, side: Side) => {
			const body =
				req.type === 'email'
					? { type: 'email', email: req.next, side }
					: {
							type: 'phone',
							phone: String(req.next).replace(/\D/g, ''),
							countryCode: req.countryCode ?? '+1',
							side,
					  };
			return await request(body);
		},
		[request]
	);

	const cancel = useCallback(
		async (req: ChangeRequest) => {
			const resp: any = await remove(
				`${API_URL.PROFILE_CHANGE_REQUESTS}/${req._id}`
			);
			await refresh();
			return resp?.data ?? resp;
		},
		[remove, refresh]
	);

	return { requests, byType, expired, loaded, refresh, request, resend, cancel };
};

/**
 * `1h54` above an hour, `54m` down to two minutes, then `90s` — the unit gets
 * finer exactly as it starts to matter. Tabular figures in the CSS keep the
 * head from shimmying as it counts.
 */
export const formatTtl = (iso: string): string => {
	const ms = msLeft(iso);
	if (ms <= 0) return 'expired';
	const mins = Math.floor(ms / 60_000);
	if (mins >= 60) return `${Math.floor(mins / 60)}h${String(mins % 60).padStart(2, '0')}`;
	if (mins >= 2) return `${mins}m`;
	return `${Math.ceil(ms / 1000)}s`;
};

/** Progressive display formatting: `+1` gets (XXX) XXX-XXXX, others group by 3. */
export const formatPhoneInput = (raw: string, countryCode = '+1'): string => {
	const d = String(raw ?? '').replace(/\D/g, '');
	if (!d) return '';
	if (countryCode !== '+1') return d.replace(/(\d{3})(?=\d)/g, '$1 ').trim();
	if (d.length <= 3) return d;
	if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
	return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6, 10)}`;
};

export const digitsOf = (v: string) => String(v ?? '').replace(/\D/g, '');
